import { useMemo, useState } from "react";
import { TrendingUp, Users } from "lucide-react";

/**
 * Lightweight SVG sparkline / area chart.
 * No chart library — 30 tiny data points, pure inline SVG for zero deps.
 */
function Sparkline({ series, valueKey, label, accent, format, testId, rangeLabel = "last 30 days" }) {
  const [hover, setHover] = useState(null);
  const width = 560;
  const height = 140;
  const padding = { top: 12, right: 8, bottom: 22, left: 32 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const values = series.map((d) => Number(d[valueKey] || 0));
  const total = values.reduce((s, v) => s + v, 0);
  const maxVal = Math.max(1, ...values);
  const step = series.length > 1 ? chartW / (series.length - 1) : chartW;

  const points = series.map((d, i) => {
    const v = Number(d[valueKey] || 0);
    const x = padding.left + i * step;
    const y = padding.top + chartH - (v / maxVal) * chartH;
    return { x, y, v, date: d.date };
  });

  const pathD = points.reduce(
    (acc, p, i) => acc + `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)} `,
    ""
  );
  const areaD =
    `${pathD} L${points[points.length - 1].x.toFixed(1)},${(padding.top + chartH).toFixed(1)} ` +
    `L${points[0].x.toFixed(1)},${(padding.top + chartH).toFixed(1)} Z`;

  // Y-axis mid + max labels
  const yTicks = [0, maxVal];

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
            {label.Icon ? <label.Icon className="w-3 h-3" /> : null}
            {label.text}
          </div>
          <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-white mt-1">
            {format ? format(total) : total}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-1">
            {rangeLabel}
          </div>
        </div>
        {hover && (
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {hover.date}
            </div>
            <div className="text-sm text-white font-mono">
              {format ? format(hover.v) : hover.v}
            </div>
          </div>
        )}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={`grad-${testId}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>

        {yTicks.map((t, i) => {
          const y = padding.top + chartH - (t / maxVal) * chartH;
          return (
            <g key={`ytick-${t}`}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="#27272a"
                strokeDasharray="2 3"
              />
              <text
                x={padding.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-zinc-600"
                style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
              >
                {format ? format(t) : t}
              </text>
            </g>
          );
        })}

        <path d={areaD} fill={`url(#grad-${testId})`} />
        <path d={pathD} fill="none" stroke={accent} strokeWidth="1.5" />

        {points.map((p, i) => (
          <g key={p.date}>
            <circle
              cx={p.x}
              cy={p.y}
              r={hover?.date === p.date ? 3.5 : 2}
              fill={accent}
              opacity={hover ? (hover.date === p.date ? 1 : 0.5) : 0.85}
            />
            {/* transparent hit rect */}
            <rect
              x={p.x - step / 2}
              y={padding.top}
              width={step}
              height={chartH}
              fill="transparent"
              onMouseEnter={() => setHover(p)}
              onFocus={() => setHover(p)}
              tabIndex={-1}
              style={{ cursor: "crosshair" }}
            />
          </g>
        ))}

        {/* X-axis first + last labels */}
        <text
          x={padding.left}
          y={height - 4}
          className="fill-zinc-600"
          style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {series[0]?.date}
        </text>
        <text
          x={width - padding.right}
          y={height - 4}
          textAnchor="end"
          className="fill-zinc-600"
          style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {series[series.length - 1]?.date}
        </text>
      </svg>
    </div>
  );
}

export default function AdminAnalytics({ signups, mrr, days = 30 }) {
  const safeSignups = useMemo(
    () => Array.isArray(signups) && signups.length ? signups : [],
    [signups]
  );
  const safeMrr = useMemo(
    () => Array.isArray(mrr) && mrr.length ? mrr : [],
    [mrr]
  );

  if (safeSignups.length === 0 && safeMrr.length === 0) return null;

  return (
    <section
      className="grid grid-cols-1 lg:grid-cols-2 gap-4"
      data-testid="admin-analytics"
    >
      {safeSignups.length > 0 && (
        <Sparkline
          series={safeSignups}
          valueKey="count"
          accent="#3b82f6"
          label={{ Icon: Users, text: "Signups" }}
          rangeLabel={`last ${days} days`}
          testId="signups-chart"
        />
      )}
      {safeMrr.length > 0 && (
        <Sparkline
          series={safeMrr}
          valueKey="mrr_usd"
          accent="#10b981"
          label={{ Icon: TrendingUp, text: "Active pack value (\u20B9)" }}
          rangeLabel={`last ${days} days`}
          format={(v) => `\u20B9${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
          testId="mrr-chart"
        />
      )}
    </section>
  );
}
